import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Tag, Store, Plus, Flame, Clock, EuroIcon, BarChart2, Edit3, Trash2 } from 'lucide-react';
import { useAppStore } from '../../shared/store';
import { generateId } from '../../shared/utils/id';
import { toast } from '../../shared/components/Toast';
import { Deal } from '../../shared/types';
import { DealDialog } from './DealDialog';
import { ConfirmDialog } from '../../shared/components/ConfirmDialog';
export function DealsPage() {
  const [selectedStore, setSelectedStore] = useState('all');
  const [isDealDialogOpen, setIsDealDialogOpen] = useState(false);
  const [dealToEdit, setDealToEdit] = useState<Deal | null>(null);
  const [dealToDelete, setDealToDelete] = useState<string | null>(null);
  const {
    activeDeals,
    inventory,
    stores,
    shoppingList,
    addShoppingListItem,
    removeDeal
  } = useAppStore();
  const activeStores = stores.filter(store => store.isActive !== false);
  const filteredDeals = useMemo(() => {
    const now = new Date();
    return activeDeals.filter(deal => {
      const isActive = new Date(deal.endDate) > now && deal.isActive;
      const store = stores.find(s => s.id === deal.retailerId);
      const matchesStore = selectedStore === 'all' || store?.name === selectedStore;
      return isActive && matchesStore && activeStores.some(s => s.id === deal.retailerId);
    }).map(deal => {
      const item = inventory.find(i => i.id === deal.itemId);
      const store = stores.find(s => s.id === deal.retailerId);
      const savingsPercentage = (deal.originalPrice - deal.discountedPrice) / deal.originalPrice * 100;
      const expiresInMilliseconds = new Date(deal.endDate).getTime() - now.getTime();
      return {
        ...deal,
        item,
        store,
        savingsPercentage,
        expiryLabel: getExpiryLabel(expiresInMilliseconds),
        expiresSoon: expiresInMilliseconds <= 3 * 24 * 60 * 60 * 1000
      };
    }).sort((a, b) => b.savingsPercentage - a.savingsPercentage);
  }, [activeDeals, inventory, stores, selectedStore, activeStores]);
  const dealMetrics = useMemo(() => {
    const now = new Date();
    const currentDeals = activeDeals.filter(deal => new Date(deal.endDate) > now && deal.isActive && activeStores.some(s => s.id === deal.retailerId));
    const totalSavings = currentDeals.reduce((sum, deal) => sum + (deal.originalPrice - deal.discountedPrice), 0);
    const averageDiscount = currentDeals.length > 0 ? currentDeals.reduce((sum, deal) => sum + (deal.originalPrice - deal.discountedPrice) / deal.originalPrice * 100, 0) / currentDeals.length : 0;
    const expiringDeals = currentDeals.filter(deal => new Date(deal.endDate).getTime() - now.getTime() < 7 * 24 * 60 * 60 * 1000).length;
    return {
      activeDeals: currentDeals.length,
      totalSavings,
      averageDiscount,
      expiringDeals
    };
  }, [activeDeals, activeStores]);
  const handleAddToList = (deal: Deal) => {
    const item = inventory.find(i => i.id === deal.itemId);
    const store = stores.find(s => s.id === deal.retailerId);
    const productName = item?.name || deal.itemName || 'Aanbieding';
    const storeName = store?.name || deal.storeName || 'Onbekend';
    const alreadyOnList = shoppingList.some(listItem => !listItem.isCompleted && listItem.name.toLowerCase() === productName.toLowerCase() && listItem.store === storeName);
    if (alreadyOnList) {
      toast.success(`${productName} staat al op je lijst`);
      return;
    }
    const savingsPercentage = (deal.originalPrice - deal.discountedPrice) / deal.originalPrice * 100;
    addShoppingListItem({
      id: generateId(),
      name: productName,
      quantity: deal.minQuantity || 1,
      unit: item?.unit || 'stuk',
      estimatedPrice: deal.discountedPrice,
      store: storeName,
      priority: 'medium',
      category: item?.category || 'Overig',
      deal: `Tot ${savingsPercentage.toFixed(0)}% korting`,
      isCompleted: false
    });
    toast.success(`${productName} toegevoegd aan je lijst`);
  };
  const handleDeleteDeal = () => {
    if (dealToDelete) {
      removeDeal(dealToDelete);
      toast.success('Aanbieding verwijderd');
      setDealToDelete(null);
    }
  };
  const handleEditDeal = (deal: Deal) => {
    setDealToEdit(deal);
    setIsDealDialogOpen(true);
  };
  const handleAddDeal = () => {
    setDealToEdit(null);
    setIsDealDialogOpen(true);
  };
  return <div className="min-h-screen bg-background pb-24" data-id="element-1545" data-mp="3fq3dzsrybmbq">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur-sm border-b border-border pt-6 pb-4 px-6 space-y-4" data-id="element-1546" data-mp="3krj2xvx92cze">
        <div className="flex items-center justify-between" data-id="element-1547" data-mp="3c7nugygg7ci7">
          <div data-id="element-1548" data-mp="388nc1h1vubvq">
            <h1 className="text-2xl font-bold text-foreground" data-id="element-1549" data-mp="3873xbuvvk8w2">Aanbiedingen</h1>
            <p className="mt-0.5 text-sm text-muted-foreground" data-id="element-1550" data-mp="3m7wu6m9nfg2c">Voorbeelddeals bij jouw winkels</p>
          </div>
          <button onClick={handleAddDeal} className="rounded-xl bg-primary p-2.5 text-primary-foreground shadow-sm transition-colors hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary" aria-label="Nieuwe aanbieding toevoegen" data-id="element-1551" data-mp="3h9vaq08b9eqc">
            <Plus className="h-5 w-5" data-id="element-1552" data-mp="339u7wf8hjx88" />
          </button>
        </div>

        {/* Store Filter */}
        {activeStores.length > 0 && <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-hide" data-id="element-1553" data-mp="2yfzl32xl324x">
            <button onClick={() => setSelectedStore('all')} className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${selectedStore === 'all' ? 'bg-primary text-primary-foreground' : 'bg-card border border-border text-muted-foreground hover:bg-accent'}`} data-id="element-1554" data-mp="2aqn8dt31sbxv">
              Alle Winkels
            </button>
            {activeStores.map(store => <button key={store.id} onClick={() => setSelectedStore(store.name)} className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${selectedStore === store.name ? 'bg-primary text-primary-foreground' : 'bg-card border border-border text-muted-foreground hover:bg-accent'}`} data-id="element-1555" data-mp="nhngrmd1uhfi">
                {store.name}
              </button>)}
          </div>}
      </div>

      <div className="px-6 pt-6 space-y-6" data-id="element-1556" data-mp="4p2vapzojelq">
        {/* Metrics Grid */}
        <div className="grid grid-cols-2 gap-3" data-id="element-1557" data-mp="1b6pw2nk7ny6u">
          <div className="bg-card border border-border rounded-xl p-3 flex items-center gap-3" data-id="element-1558" data-mp="1g62l9eyk1hcl">
            <div className="p-2 bg-primary/10 text-primary rounded-lg shrink-0" data-id="element-1559" data-mp="1q9zsji89xmf1">
              <Tag className="w-4 h-4" data-id="element-1560" data-mp="1qc3s5nz53z9i" />
            </div>
            <div data-id="element-1561" data-mp="1lchdrb7kscz4">
              <p className="text-xs text-muted-foreground" data-id="element-1562" data-mp="1lby74qndlb0e">Aanbiedingen nu</p>
              <p className="text-lg font-bold text-foreground" data-id="element-1563" data-mp="1gj52s3vtk4ou">
                {dealMetrics.activeDeals}
              </p>
            </div>
          </div>
          <div className="bg-card border border-border rounded-xl p-3 flex items-center gap-3" data-id="element-1564" data-mp="17ji1rj7050wb">
            <div className="p-2 bg-success/10 text-success rounded-lg shrink-0" data-id="element-1565" data-mp="12koptv7d5hy9">
              <EuroIcon className="w-4 h-4" data-id="element-1566" data-mp="12n1essbjgskt" />
            </div>
            <div data-id="element-1567" data-mp="xmdd9cpootcc">
              <p className="text-xs text-muted-foreground" data-id="element-1568" data-mp="xlviylml0p72">Je kunt besparen</p>
              <p className="text-lg font-bold text-foreground" data-id="element-1569" data-mp="tlbl1fu6phjv">
                €{dealMetrics.totalSavings.toFixed(2)}
              </p>
            </div>
          </div>
          <div className="bg-card border border-border rounded-xl p-3 flex items-center gap-3" data-id="element-1570" data-mp="2nxi0wry0wmii">
            <div className="p-2 bg-primary/10 text-primary rounded-lg shrink-0" data-id="element-1571" data-mp="3769gwpzm4xf0">
              <BarChart2 className="w-4 h-4" data-id="element-1572" data-mp="3b8d2i7fzxie0" />
            </div>
            <div data-id="element-1573" data-mp="3b7l7oyu83oos">
              <p className="text-xs text-muted-foreground" data-id="element-1574" data-mp="3ba7g10oxu882">Gemiddelde korting</p>
              <p className="text-lg font-bold text-foreground" data-id="element-1575" data-mp="2xgpx9i42szj3">
                {dealMetrics.averageDiscount.toFixed(0)}%
              </p>
            </div>
          </div>
          <div className="bg-card border border-border rounded-xl p-3 flex items-center gap-3" data-id="element-1576" data-mp="2nhquuh9wm04j">
            <div className="p-2 bg-warning/10 text-warning rounded-lg shrink-0" data-id="element-1577" data-mp="2siy30hfyl1bq">
              <Clock className="w-4 h-4" data-id="element-1578" data-mp="2eing1phrs6vu" />
            </div>
            <div data-id="element-1579" data-mp="2ek6r91okjrqr">
              <p className="text-xs text-muted-foreground" data-id="element-1580" data-mp="2jh6wxbhiashc">Verloopt Snel</p>
              <p className="text-lg font-bold text-foreground" data-id="element-1581" data-mp="2jjb4c3em69md">
                {dealMetrics.expiringDeals}
              </p>
            </div>
          </div>
        </div>

        {filteredDeals.length === 0 ? <div className="py-12 text-center" data-id="element-1582" data-mp="rts663nl6v9s">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-accent" data-id="element-1583" data-mp="rutrd0ybgy58">
              <Tag className="h-8 w-8 text-muted-foreground" data-id="element-1584" data-mp="mw8th86jeqjy" />
            </div>
            <p className="font-semibold text-foreground" data-id="element-1585" data-mp="10n45uug8vpsa">Nog geen aanbiedingen</p>
            <p className="mx-auto mt-1 max-w-xs text-sm leading-relaxed text-muted-foreground" data-id="element-1586" data-mp="vuu6knt8pddc">
              Voeg een aanbieding toe, of laad demo-aanbiedingen via Instellingen om de flow te bekijken.
            </p>
          </div> : <AnimatePresence data-id="element-1587" data-mp="15kxbnmdmyhs1">
            {filteredDeals.map(deal => {
          const productName = deal.item?.name || deal.itemName || 'Aanbieding';
          const isHot = deal.savingsPercentage >= 40;
          return <motion.div layout initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            scale: 0.95
          }} transition={{
            duration: 0.2
          }} key={deal.id} className={`flex flex-col gap-3 rounded-2xl border bg-card p-4 shadow-sm ${isHot ? 'border-warning/50 shadow-warning/10' : 'border-border'}`} data-id="element-1588" data-mp="3lc1mdovyuzr0">
                  <div className="flex justify-between items-start gap-3" data-id="element-1589" data-mp="169heewza9lhc">
                    <div className="flex-1 min-w-0" data-id="element-1590" data-mp="119d9m47qmf6y">
                      <div className="flex items-center gap-2 mb-1" data-id="element-1591" data-mp="whbn7pguv837">
                        <h3 className="font-medium text-foreground truncate" data-id="element-1592" data-mp="wfii4elw4vxb">
                          {productName}
                        </h3>
                        <div className="shrink-0 rounded bg-success/10 px-1.5 py-0.5 text-[10px] font-bold text-success" data-id="element-1593" data-mp="1oz7jqe0o9pni">
                          -{deal.savingsPercentage.toFixed(0)}%
                        </div>
                        {deal.isDemo && <span className="shrink-0 rounded bg-accent px-1.5 py-0.5 text-[10px] font-semibold text-muted-foreground" data-id="element-1594" data-mp="1whp116esi8r1">
                            Demo
                          </span>}
                        {isHot && <Flame className="w-4 h-4 text-destructive flex-shrink-0" data-id="element-1595" data-mp="26h62tcqx82hq" />}
                      </div>

                      <div className="flex items-center gap-2 text-xs text-muted-foreground" data-id="element-1596" data-mp="2fg9moixwwlxa">
                        <span className="flex items-center gap-1 bg-accent px-1.5 py-0.5 rounded" data-id="element-1597" data-mp="2k79ghfbqinvk">
                          <Store className="w-3 h-3" data-id="element-1598" data-mp="2p6cmp33gb054" />{' '}
                          {deal.store?.name || deal.storeName}
                        </span>
                        {deal.expiresSoon && <span className="flex items-center gap-1 text-warning" data-id="element-1599" data-mp="2y6rcofrd13ey">
                            <Clock className="w-3 h-3" data-id="element-1600" data-mp="2y4oayyguhlue" /> {deal.expiryLabel}
                          </span>}
                      </div>
                    </div>

                    <div className="flex items-center gap-1 shrink-0 -mt-1 -mr-1" data-id="element-1601" data-mp="u1390ynkcm03">
                      <button onClick={() => handleEditDeal(deal)} className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors" data-id="element-1602" data-mp="17w5mc6uqpdmx">
                        <Edit3 className="w-4 h-4" data-id="element-1603" data-mp="1ctxp5vwxak5w" />
                      </button>
                      <button onClick={() => setDealToDelete(deal.id)} className="p-1.5 text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded-lg transition-colors" data-id="element-1604" data-mp="1cw1p62rpaz0w">
                        <Trash2 className="w-4 h-4" data-id="element-1605" data-mp="3sllcczm4wybi" />
                      </button>
                    </div>
                  </div>

                  <div className="flex items-end justify-between mt-1" data-id="element-1606" data-mp="3olkv777ni90r">
                    <div data-id="element-1607" data-mp="3eftd4igdjs7y">
                      <div className="flex items-baseline gap-2" data-id="element-1608" data-mp="3ehnph8dkyc0f">
                        <span className="text-xl font-bold text-foreground" data-id="element-1609" data-mp="3igdb1o8ruzv5">
                          €{deal.discountedPrice.toFixed(2)}
                        </span>
                        <span className="text-sm text-muted-foreground line-through" data-id="element-1610" data-mp="39nru8dopb38y">
                          €{deal.originalPrice.toFixed(2)}
                        </span>
                      </div>
                      {deal.type === 'bulk' && deal.minQuantity && <p className="mt-0.5 text-xs text-primary" data-id="element-1611" data-mp="2lqm6sxaohyc5">
                          Je koopt minimaal {deal.minQuantity} stuks
                        </p>}
                    </div>

                    <button onClick={() => handleAddToList(deal)} className="flex items-center justify-center gap-1.5 rounded-xl bg-primary px-3 py-2 text-sm font-semibold text-primary-foreground shadow-sm transition-colors hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary" aria-label="Toevoegen aan lijst" data-id="element-1612" data-mp="jypuvmklmmgu">
                      <Plus className="h-4 w-4" data-id="element-1613" data-mp="2b29kmk15mgag" />
                      {deal.type === 'bulk' && deal.minQuantity ? `Toevoegen (${deal.minQuantity}x)` : 'Toevoegen'}
                    </button>
                  </div>
                </motion.div>;
        })}
          </AnimatePresence>}
      </div>

      <DealDialog isOpen={isDealDialogOpen} onClose={() => setIsDealDialogOpen(false)} dealToEdit={dealToEdit} data-id="element-1614" data-mp="1scjs63kv66lp" />

      <ConfirmDialog isOpen={!!dealToDelete} title="Aanbieding Verwijderen" description="Weet je zeker dat je deze aanbieding wilt verwijderen? Dit kan niet ongedaan worden gemaakt." confirmText="Verwijderen" cancelText="Annuleren" variant="danger" onConfirm={handleDeleteDeal} onCancel={() => setDealToDelete(null)} data-id="element-1615" data-mp="1nf1drxo2uz6b" />
    </div>;
}
function getExpiryLabel(expiresInMilliseconds: number): string {
  const hoursLeft = Math.max(0, Math.ceil(expiresInMilliseconds / (1000 * 60 * 60)));
  if (hoursLeft < 24) {
    return hoursLeft <= 1 ? 'Verloopt binnen 1 uur' : `Nog ${hoursLeft} uur`;
  }
  const daysLeft = Math.floor(hoursLeft / 24);
  return daysLeft === 1 ? 'Verloopt morgen' : `Nog ${daysLeft} dagen`;
}